import type React from 'react';
import type { PaddleConfig } from './components/billing/paddle.js';

/**
 * What a page can ask the extension's background worker to do: reschedule the reminder, redraw
 * the toolbar badge, or sync with the server now (a page that is closing may not get to finish).
 */
export type BackgroundNotice = { type: 'UPDATE_ALARM' | 'REFRESH_BADGE' | 'SYNC_NOW' };

/**
 * The few places where the dashboard behaves differently depending on where it runs. The values
 * below are the extension's own; the website (packages/app) replaces them with `setPlatform`
 * before it renders anything.
 */
export interface DashboardPlatform {
  /** A control in the top bar next to "Add word". The extension's dashboard has none (reviews
   * start from the toolbar popup); the website shows "Review in the extension". */
  ReviewAction: React.ComponentType | null;
  /** A notice above the page content (the website's "install the extension" banner). */
  Notice: React.ComponentType | null;
  /** Shown on the last step of the first-run flow, before the dashboard. The website tells a
   * visitor where reviews happen and how to get the extension. Without it the flow ends at once. */
  AfterOnboarding: React.ComponentType | null;
  /** An extra card at the end of the Settings tab (the staging website's testing tools). */
  SettingsExtras: React.ComponentType | null;
  /** Below the sign-in card: the website's legal links (Paddle's review needs them there). */
  SignInFooter: React.ComponentType | null;
  /** Passes a notice to the background worker. The website has no worker to tell. */
  notifyBackground: (notice: BackgroundNotice) => void;
  /**
   * Whether a second meaning of a word may be made into its own card here. The extension: always.
   * The website: only when the learner's installed extension understands meanings too, because an
   * older extension would not show such a card in reviews.
   */
  sensesEnabled: () => Promise<boolean>;
  /**
   * Paddle's client-side token and environment for the subscribe page's checkout. Only the
   * website has them (from its build settings); null means "checkout isn't available here".
   */
  paddle: PaddleConfig | null;
}

export const platform: DashboardPlatform = {
  ReviewAction: null,
  Notice: null,
  AfterOnboarding: null,
  SettingsExtras: null,
  SignInFooter: null,
  notifyBackground: (notice) => {
    if (typeof chrome !== 'undefined' && chrome.runtime?.sendMessage) {
      chrome.runtime.sendMessage(notice);
    }
  },
  sensesEnabled: async () => true,
  paddle: null,
};

/** Replaces parts of the platform. Call once at start-up, before the first render. */
export function setPlatform(overrides: Partial<DashboardPlatform>): void {
  Object.assign(platform, overrides);
}
